/**
 * @file AddClientTabsPage.js
 * @description Component for adding a new client with multiple tabs.
 **/
import { useState, useCallback } from 'react';
import { useAuthContext } from '../../store/AuthContext';
import { useNavigate } from 'react-router-dom';
import ClientTab from './ClientTab';
import DetailsTab from './DetailsTab';
import SelectionTab from './SelectionTab';
import OrgLevelsTab from './OrgLevelsTab';
import { getClient, createClient, createClientOption } from '../../services/clientService';
import styles from './AddClientPage.module.scss';
import Toast from '../ui/toasts/Toast';
import { checkAndRefreshToken } from '../../utils/tokenRefresh';
import { isValidZipCode } from '../../utils/validate';
import { ROUTES } from '../../constants/routes';
import { ClientOptionEnum } from '../../utils/data/enums';
import { getText } from '../../i18n';

const TABS = {
    CLIENT: 'Client',
    DETAILS: 'Details',
    SELECTION: 'Selection',
    ORG_LEVELS: 'Org Levels',
};

const AddClientTabsPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(TABS.DETAILS);
    const [clientData, setClientData] = useState({});
    const [clientTabData, setClientTabData] = useState({});
    const [detailsTabData, setDetailsTabData] = useState({});
    const [selectionTabData, setSelectionTabData] = useState({});
    const [orgLevelsTabData, setOrgLevelsTabData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isSaveLoading, setIsSaveLoading] = useState(false);
    const { user } = useAuthContext();

    const validateClientTab = () => {
        if (!clientTabData.copyFrom) {
            Toast.error(getText('msg_d087ae13af42'));
            return false;
        }
        return true;
    };

    const validateDetailsTab = () => {
        if (!detailsTabData.code) {
            Toast.error(getText('msg_48e1eb9a36ed'));
            return false;
        } else if (!detailsTabData.name) {
            Toast.error(getText('msg_7295fd089ced'));
            return false;
        } else if (!detailsTabData.tenantTierId) {
            Toast.error(getText('msg_51ae9405cb8c'));
            return false;
        } else if (!detailsTabData.tenantStatusId) {
            Toast.error(getText('msg_8716d9ee2507'));
            return false;
        } else if (!detailsTabData.agencyId) {
            Toast.error(getText('msg_4a6acab51516'));
            return false;
        } else if (
            detailsTabData.address?.zip &&
            !isValidZipCode(detailsTabData.address.zip)
        ) {
            Toast.error(
                getText('msg_1f8b44a52003'),
            );
            return false;
        }
        return true;
    };

    const validateTab = tab => {
        switch (tab) {
            case TABS.CLIENT:
                return validateClientTab();
            case TABS.DETAILS:
                return validateDetailsTab();
            case TABS.SELECTION:
                return true;
            default:
                return true;
        }
    };

    const updateClientData = (tab, data) => {
        switch (tab) {
            case TABS.CLIENT:
                setClientData({ ...clientData, ...data });
                break;
            case TABS.DETAILS:
                setClientData(prevData => ({ ...prevData, ...data }));
                break;
            case TABS.SELECTION:
                setClientData(prevData => ({
                    ...prevData,
                    selection: {
                        ...prevData.selection,
                        ...data,
                    },
                }));
                break;
            case TABS.ORG_LEVELS:
                setClientData(prevData => ({
                    ...prevData,
                    orgLevel: {
                        ...prevData.orgLevel,
                        ...data,
                    },
                }));
                break;
            default:
                break;
        }
    };

    const handleNext = async () => {
        if (!validateTab(activeTab)) return;

        if (activeTab === TABS.CLIENT && clientTabData.copyFrom) {
            try {
                setIsLoading(true);
                const {
                    code,
                    name,
                    agency,
                    createdBy,
                    createdAt,
                    updatedBy,
                    updatedAt,
                    contacts,
                    ...copiedClient
                } = await getClient(clientTabData.copyFrom);
                setDetailsTabData(copiedClient);
            } catch (error) {
                Toast.error(getText('msg_286383794e31'));
                return;
            } finally {
                setIsLoading(false);
            }
        }

        updateClientData(
            activeTab,
            activeTab === TABS.CLIENT
                ? clientTabData
                : activeTab === TABS.SELECTION
                    ? selectionTabData
                    : detailsTabData,
        );

        const nextTab =
            Object.values(TABS)[Object.values(TABS).indexOf(activeTab) + 1];
        setActiveTab(nextTab);
    };

    const handleTabChange = async tab => {
        const currentTabIndex = Object.values(TABS).indexOf(activeTab);
        const clickedTabIndex = Object.values(TABS).indexOf(tab);

        if (clickedTabIndex > currentTabIndex) {
            for (let i = currentTabIndex; i < clickedTabIndex; i++) {
                const tabToValidate = Object.values(TABS)[i];
                if (!validateTab(tabToValidate)) return;
            }
        }

        if (activeTab === TABS.CLIENT && clientTabData.copyFrom) {
            try {
                setIsLoading(true);
                const copiedClient = await getClient(clientTabData.copyFrom);
                setDetailsTabData(copiedClient);
            } catch (error) {
                Toast.error(getText('msg_286383794e31'));
                return;
            } finally {
                setIsLoading(false);
            }
        }

        updateClientData(
            activeTab,
            activeTab === TABS.CLIENT ? clientTabData : detailsTabData,
        );

        setActiveTab(tab);
    };

    const handleClientTabChange = useCallback(data => {
        setClientTabData(data);
    }, []);

    const handleDetailsTabChange = useCallback(data => {
        setDetailsTabData(data);
    }, []);

    const handleSelectionTabChange = useCallback(data => {
        setSelectionTabData(data);
    }, []);

    const handleOrgLevelsTabChange = useCallback(data => {
        setOrgLevelsTabData(data);
        setClientData(prevClientData => ({
            ...prevClientData,
            orgLevel: data,
        }));
    }, []);

    const handleSave = async () => {
        if (!validateTab(activeTab)) return;

        setIsSaveLoading(true);

        try {
            const payload = { ...detailsTabData, isActive: true };
            await createClient(payload);
        } catch (error) {
            console.error('Unable to save changes', error);
            if (error?.response?.data?.detail) {
                Toast.error(error?.response?.data?.detail);
            } else {
                Toast.error(getText('msg_35ce61933971'));
            }
            setIsSaveLoading(false);
            return;
        }

        try {
            const { first_name, last_name } = user;
            await checkAndRefreshToken(true, first_name, last_name);
        } catch (error) {
            Toast.info(
                getText('msg_3b8b7563b4d5'),
                { autoClose: false },
            );
            setIsSaveLoading(false);
            navigate(ROUTES.CLIENTS);
            return;
        }

        Toast.success(getText('msg_39810ad2ba4b'));
        navigate(ROUTES.CLIENTS);
    };

    return (
        <div className={`page-content ${styles.container}`}>
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center position-relative pb-3">
                        <h2 className="page-title float-start ms-4 m-0 col-2">
                            {getText('msg_d2bacebb979a')}
                        </h2>
                        <div className="col-2">
                            <span
                                type="button"
                                onClick={() => navigate(ROUTES.CLIENTS)}
                                className="d-flex ing-text-buttons mx-3 ms-3 me-4 float-end"
                            >
                                <i className="bi bi-x me-2" />
                                {getText('msg_bbfa773e5a63')}
                            </span>
                        </div>
                    </div>

                    <div className="w-100 mt-5 pb-5">
                        <div className={styles.tabContent}>
                            {activeTab === TABS.CLIENT && (
                                <ClientTab
                                    data={clientTabData}
                                    onChange={handleClientTabChange}
                                />
                            )}
                            {activeTab === TABS.DETAILS && (
                                <DetailsTab
                                    data={detailsTabData}
                                    onChange={handleDetailsTabChange}
                                />
                            )}
                            {activeTab === TABS.SELECTION && (
                                <SelectionTab
                                    data={selectionTabData}
                                    onChange={handleSelectionTabChange}
                                />
                            )}
                            {activeTab === TABS.ORG_LEVELS && (
                                <OrgLevelsTab
                                    data={orgLevelsTabData}
                                    onChange={handleOrgLevelsTabChange}
                                />
                            )}
                        </div>

                        <div className="container">
                            <div className="row mx-3">
                                <div className="d-flex justify-content-between">
                                    <div className="col-12 col-xs-12 col-md-12 col-lg-6">
                                        <p>
                                            <span className="text-danger">*</span>{' '}
                                            {getText('msg_7c118f042eaa')}
                                        </p>
                                    </div>
                                    <div className="col-12 col-xs-12 col-md-12 col-lg-6">
                                        <button
                                            type="button"
                                            className="float-end button-base ing-button-primary-left ms-3 me-4 p-auto"
                                            onClick={handleSave}
                                            disabled={isSaveLoading}
                                        >
                                            {isSaveLoading ? 'Saving...' : 'Save'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddClientTabsPage;
